import React, { useState, useEffect } from 'react'
import BreadCrumbs from '../../../../components/common/BreadCrumbs/BreadCrumbs'
import { useNavigate } from 'react-router-dom'
import SearchForm from './SearchForm'
import { MANUAL_TIME_OFF_SEARCH_CONFIG } from '../../../../components/common/config/adminConfig'
import EmployeeResultsTable from './EmployeeResultsTable'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { useRoleGuard } from '../../../../hooks/useRoleGaurd'
import ValidationBanner from '../../../../components/common/ValidationBanner'
import Loader from '../../../../components/common/Loader'
import {
  searchManualTimeOffEmployees,
  resetManualTimeOffSearch,
} from '../../../../store/slices/admin/manualTimeOffSlice'

/** Matches legacy's RPSearchSelectEmployeeStaticView message, shown in place of
 * the search form when the signed-in user is not an authorized Requestor Proxy. */
const NOT_REQUESTOR_PROXY_MESSAGE =
  'Please sign in as an authorized Requestor Proxy of one or more approval groups in order to search and select employees for submitting Time Off Requests on behalf of them.'

/** Allows only alphanumeric characters (0-9, a-z, A-Z) — no spaces or punctuation. */
const STRICT_ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]*$/

/** Shared validation message for disallowed input. */
const ALPHANUMERIC_ERROR_MESSAGE =
  'Please only enter alphanumeric characters (0-9 a-z A-Z).'

/** Minimum characters required for first/last name search fields, matching the API's own min(2) validation. */
const NAME_MIN_LENGTH = 2

/**
 * ManualTimeOff page — allows a requestor proxy to search for employees
 * within their approval group and submit a time-off request on their behalf.
 * Waits for role data before displaying content, access feedback, or admin navigation.
 *
 * Route: /admin/manual-timeoff
 */
const ManualTimeOff = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {
    hasAccess: isRequestorProxy,
    loading: roleLoading,
    initialized: roleInitialized,
    error: roleError,
    isAdmin,
    isAPAdmin,
    isFEAdmin,
    isRPAdmin,
  } = useRoleGuard(['requestorProxy'])
  const hasProfileRoles = useAppSelector((state) => Boolean(state.profile.data?.roles))
  const roleDetermined = roleInitialized && !roleLoading && (hasProfileRoles || Boolean(roleError))
  const showAdminBreadcrumb = isAdmin || isAPAdmin || isFEAdmin || isRPAdmin

  const { isLoading, searchPerformed, results, error } = useAppSelector(
    (state) => state.manualTimeOff
  )

  const [searchBy, setSearchBy] = useState<string>('name')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [nuid, setNuid] = useState<string>('')
  const [errors, setErrors] = useState<Record<string, string>>({})  

  const values = { firstName, lastName, nuid }
  /** Reset Redux search state when the component unmounts. */
  useEffect(() => {
    return () => {
      dispatch(resetManualTimeOffSearch())
    }
  }, [dispatch])

  const handlePreviousClick = () => navigate('/admin')

  /** Clears field-level validation errors and resets search results when the search type changes. */
  const handleSearchTypeChange = (type: string) => {
    setSearchBy(type)
    setNuid('')
    setFirstName('')
    setLastName('')
    setErrors({})
  }

  /**
   * Updates the relevant field state and re-validates that field.
   * Only recomputes when the field already has a visible error, so a value
   * that's still invalid after an edit (e.g. one special character removed
   * from several) keeps showing an error instead of clearing prematurely.
   * Clearing the field back to empty always clears the error immediately
   * rather than reactively surfacing a "required" message — that check only
   * runs again on the next Search click.
   */
  const handleFieldChange = (fieldName: string, value: string) => {
    /** Lookup map of field name → state setter. */
    const fieldSetters: Record<string, (v: string) => void> = {
      firstName: setFirstName,
      lastName: setLastName,
      nuid: setNuid,
    }
    fieldSetters[fieldName]?.(value)

    setErrors((prev) => {
      if (!prev[fieldName]) return prev

      const trimmedValue = value.trim()
      const { [fieldName]: _, ...rest } = prev
      if (!trimmedValue) return rest

      const nextFirstName = fieldName === 'firstName' ? trimmedValue : firstName.trim()
      const nextLastName = fieldName === 'lastName' ? trimmedValue : lastName.trim()
      const nextNuid = fieldName === 'nuid' ? trimmedValue : nuid.trim()

      const revalidated = buildValidationErrors(nextFirstName, nextLastName, nextNuid)
      return revalidated[fieldName] ? { ...rest, [fieldName]: revalidated[fieldName] } : rest
    })
  }

  /**
   * Validates a single name field: alphanumeric first, then minimum length.
   * Returns the error message for the field, or undefined when valid/empty.
   */
  const validateNameField = (
    trimmedValue: string,
    fieldLabel: 'First name' | 'Last name'
  ): string | undefined => {
    if (!trimmedValue) return undefined
    if (!STRICT_ALPHANUMERIC_REGEX.test(trimmedValue)) return ALPHANUMERIC_ERROR_MESSAGE
    if (trimmedValue.length < NAME_MIN_LENGTH) {
      return `${fieldLabel} must be at least ${NAME_MIN_LENGTH} characters`
    }
    return undefined
  }

  /**
   * Builds validation errors for the current search mode.
   *
   * - Name search: at least first or last name required; both must be
   *   alphanumeric and, when provided, at least NAME_MIN_LENGTH characters.
   * - NUID search: NUID is required and must be alphanumeric.
   */
  const buildValidationErrors = (
    trimmedFirstName: string,
    trimmedLastName: string,
    trimmedNuid: string
  ): Record<string, string> => {
    const nextErrors: Record<string, string> = {}

    if (searchBy === 'name') {
      if (!trimmedFirstName && !trimmedLastName) {
        nextErrors.firstName = 'Enter first or last name'
        return nextErrors
      }

      const firstNameError = validateNameField(trimmedFirstName, 'First name')
      if (firstNameError) nextErrors.firstName = firstNameError

      const lastNameError = validateNameField(trimmedLastName, 'Last name')
      if (lastNameError) nextErrors.lastName = lastNameError
    } else if (searchBy === 'nuid') {
      if (!trimmedNuid) {
        nextErrors.nuid = 'NUID is required'
      } else if (!STRICT_ALPHANUMERIC_REGEX.test(trimmedNuid)) {
        nextErrors.nuid = ALPHANUMERIC_ERROR_MESSAGE
      }
    }

    return nextErrors
  }

  /**
   * Validates inputs then dispatches the employee search API call.
   * Last Name is always sent; First Name is sent alongside it and narrows
   * the match when provided (matching the Approver/Requestor Proxy pattern).
   * The `searchValue` must be at least 2 characters (enforced by the API).
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedFirstName = firstName.trim()
    const trimmedLastName = lastName.trim()
    const trimmedNuid = nuid.trim()

    const nextErrors = buildValidationErrors(trimmedFirstName, trimmedLastName, trimmedNuid)

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    if (searchBy === 'name') {
      dispatch(
        searchManualTimeOffEmployees({
          searchOption: 'name',
          searchFName: trimmedFirstName,
          searchLName: trimmedLastName,
        })
      )
    } else {
      dispatch(
        searchManualTimeOffEmployees({ searchOption: 'nuid', searchValue: trimmedNuid })
      )
    }
  }

  if (!roleDetermined) return <Loader />

  if (!isRequestorProxy) {
    return (
      <div className="pt-6 pb-6 page-container">
        {showAdminBreadcrumb ? (
          <BreadCrumbs previousPage="Administration" onPreviousClick={handlePreviousClick} />
        ) : (
          <div className="mobile-margin lg:!mt-[60px]" />
        )}

        <div className="flex flex-col items-center justify-center min-h-100 p-4">
          <div className="w-full max-w-4xl text-left">
            <ValidationBanner
              variant="error"
              heading={<p className="p1 -medium">Submit a Time Off Request for an Employee</p>}
              message={<p className="p3 -book mt-2">{NOT_REQUESTOR_PROXY_MESSAGE}</p>}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-6 pb-6 page-container">
      {showAdminBreadcrumb ? (
        <BreadCrumbs
          previousPage="Administration"
          currentPage="Submit a Time Off Request for an Employee"
          onPreviousClick={handlePreviousClick}
        />
      ) : (
        // Preserves BreadCrumbs' own top-level spacing so the page doesn't jump when it's hidden.
        <div className="mobile-margin lg:!mt-[60px]" />
      )}
      <h2 className="h2 mb-4">Submit a Time Off Request for an Employee</h2>

      <SearchForm
        searchOptions={MANUAL_TIME_OFF_SEARCH_CONFIG.searchOptions}
        fieldsByMode={MANUAL_TIME_OFF_SEARCH_CONFIG.fieldsByMode}
        fieldMeta={MANUAL_TIME_OFF_SEARCH_CONFIG.fieldMeta}
        values={values}
        errors={errors}
        searchBy={searchBy}
        onSearchTypeChange={handleSearchTypeChange}
        onFieldChange={handleFieldChange}
        onSearch={handleSearch}
        isLoading={isLoading}
        columnLayout={true}
        formId="manualTimeOffForm"
      />

      {error && (
        <div role="alert" className="mt-6 text-red-700 p3">
          {error}
        </div>
      )}

      {searchPerformed && !error && (
        <div className="mt-8">
          {results.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No employees found matching your search criteria.</p>
            </div>
          ) : (
            <EmployeeResultsTable results={results} />
          )}
        </div>
      )}
    </div>
  )
}

export default ManualTimeOff
